// frontend/src/components/dialogs/SaveShortcutSegmentsDialog.jsx
// Dialog for saving shortcut segments from trips with reordered waypoints
// See docs/ETA_TRIP_TRACKING_REORDERABLE_WAYPOINTS.md

import { useState, useEffect, useCallback } from 'react';
import { Route, AlertCircle, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import shortcutSegmentService from '../../services/shortcutSegmentService';
import { formatDateShort } from '../../utils/dateTimeFormatter';
import { buildSegmentKey } from '../../utils/segmentUtils';
import ConfirmDialogShell from './ConfirmDialogShell';

/**
 * Dialog for saving shortcut segment references from a completed trip with reordered waypoints.
 *
 * Props:
 * - isOpen: boolean - Whether dialog is open
 * - onClose: function - Called when dialog closes (with result or null)
 * - entityId: number - Entity ID
 * - tripId: number - Trip ID (must be completed)
 * - routeId: number - Route ID (optional, will be fetched if not provided)
 * - onSaveComplete: function - Called after successful save with result
 */
const SaveShortcutSegmentsDialog = ({
    isOpen,
    onClose,
    entityId,
    tripId,
    routeId: propRouteId,
    onSaveComplete
}) => {
    const { t } = useTranslation();
    const { background, text, border, semantic, cn } = useTheme();

    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Data from API
    const [shortcutEdges, setShortcutEdges] = useState([]);
    const [tripArchivedType, setTripArchivedType] = useState('representative');

    // Checkbox states: { edgeKey: { representative: bool, [tripType]: bool } }
    const [checkboxStates, setCheckboxStates] = useState({});

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Step 1: Detect shortcut edges for this trip
            const edgeResult = await shortcutSegmentService.detectShortcutEdges(entityId, tripId);

            if (!edgeResult.success) {
                setError(edgeResult.error || 'Failed to detect shortcut edges');
                return;
            }

            if (!edgeResult.hasReorderedWaypoints || edgeResult.shortcutEdges.length === 0) {
                // No shortcuts to save - close dialog
                toast.info(t('noShortcutEdgesDetected') || 'No shortcut edges detected for this trip');
                onClose(null);
                return;
            }

            setShortcutEdges(edgeResult.shortcutEdges);
            setTripArchivedType(edgeResult.tripArchivedType || 'representative');

            // Step 2: Get existing shortcuts for these edges
            const existingResult = await shortcutSegmentService.getExistingShortcuts(
                entityId,
                edgeResult.routeId || propRouteId,
                edgeResult.shortcutEdges.map(e => ({
                    fromWaypointId: e.fromWaypointId,
                    toWaypointId: e.toWaypointId
                }))
            );

            if (existingResult.success) {
                // Step 3: Calculate default checkbox states
                const defaults = shortcutSegmentService.getDefaultCheckboxStates(
                    edgeResult.shortcutEdges,
                    existingResult.existingShortcuts || {},
                    edgeResult.tripArchivedType || 'representative'
                );
                setCheckboxStates(defaults);
            }

        } catch (err) {
            console.error('Error loading shortcut data:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load shortcut data');
        } finally {
            setLoading(false);
        }
    }, [entityId, onClose, propRouteId, t, tripId]);

    // Load shortcut edges and existing shortcuts when dialog opens
    useEffect(() => {
        if (isOpen && entityId && tripId) {
            loadData();
        }
    }, [entityId, isOpen, loadData, tripId]);

    // Handle checkbox change for an edge
    const handleCheckboxChange = useCallback((edgeKey, archivedType, checked) => {
        setCheckboxStates(prev => ({
            ...prev,
            [edgeKey]: {
                ...prev[edgeKey],
                [archivedType]: checked
            }
        }));
    }, []);

    // Check if at least one checkbox is selected
    const hasSelection = useCallback(() => {
        for (const edgeKey of Object.keys(checkboxStates)) {
            const state = checkboxStates[edgeKey];
            if (state.representative || state[tripArchivedType]) {
                return true;
            }
        }
        return false;
    }, [checkboxStates, tripArchivedType]);

    // Get selected edge/type targets from checkbox states
    const getSelectedTargets = useCallback(() => {
        const edgesByKey = new Map(shortcutEdges.map(edge => [
            buildSegmentKey(edge.fromWaypointId, edge.toWaypointId),
            edge
        ]));
        const targets = [];

        for (const edgeKey of Object.keys(checkboxStates)) {
            const state = checkboxStates[edgeKey];
            const edge = edgesByKey.get(edgeKey);
            if (!edge) {
                continue;
            }
            if (state.representative) {
                targets.push({
                    fromWaypointId: edge.fromWaypointId,
                    toWaypointId: edge.toWaypointId,
                    archivedType: 'representative',
                    variant: null
                });
            }
            if (state[tripArchivedType] && tripArchivedType !== 'representative') {
                targets.push({
                    fromWaypointId: edge.fromWaypointId,
                    toWaypointId: edge.toWaypointId,
                    archivedType: tripArchivedType,
                    variant: null
                });
            }
        }

        return targets;
    }, [checkboxStates, shortcutEdges, tripArchivedType]);

    const hasReplacementSelection = useCallback(() => (
        Object.values(checkboxStates).some(state => (
            (state.representative && state.representativeExists)
            || (tripArchivedType !== 'representative' && state[tripArchivedType] && state.tripTypeExists)
        ))
    ), [checkboxStates, tripArchivedType]);

    // Handle save
    const handleSave = async () => {
        if (!hasSelection()) {
            toast.warning(t('selectAtLeastOneTarget') || 'Please select at least one target for at least one edge');
            return;
        }

        try {
            setSaving(true);

            const targets = getSelectedTargets();
            const result = await shortcutSegmentService.saveShortcuts(entityId, tripId, targets);

            if (result.success) {
                const savedCount = result.saved?.length || 0;
                const skippedCount = result.skipped?.length || 0;

                if (savedCount > 0) {
                    toast.success(
                        t('shortcutSegmentsSaved', { count: savedCount }) ||
                        `Saved ${savedCount} shortcut segment(s)`
                    );
                }

                if (skippedCount > 0) {
                    toast.warning(
                        t('shortcutSegmentsSkipped', { count: skippedCount }) ||
                        `Skipped ${skippedCount} edge(s) due to insufficient data`
                    );
                }

                if (onSaveComplete) {
                    onSaveComplete(result);
                }
                onClose(result);
            } else {
                toast.error(result.error || 'Failed to save shortcut segments');
            }
        } catch (err) {
            console.error('Error saving shortcuts:', err);
            toast.error(err.response?.data?.error || err.message || 'Failed to save shortcuts');
        } finally {
            setSaving(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (!saving) {
            onClose(null);
        }
    };

    return (
        <ConfirmDialogShell
            isOpen={isOpen}
            onClose={handleCancel}
            title={t('saveShortcutSegments') || 'Save Shortcut Segments'}
            size="lg"
            closeOnBackdropClick={!saving}
            onConfirm={handleSave}
            confirmLabel={saving ? (t('saving') || 'Saving...') : (t('confirm') || 'Confirm')}
            confirmVariant={hasReplacementSelection() ? 'warning' : 'primary'}
            confirmIcon={hasReplacementSelection() ? AlertTriangle : Check}
            isLoading={saving}
            isDisabled={loading || !hasSelection()}
            showIcon={hasReplacementSelection()}
            iconVariant="warning"
        >
            <div className="space-y-4">
                {/* Information section */}
                <div className={cn("border rounded-lg p-3", semantic.info.background, border.input, semantic.info.text)}>
                    <div className="flex items-start gap-2">
                        <AlertCircle className={cn("w-5 h-5 flex-shrink-0 mt-0.5", semantic.info.accent)} />
                        <div className="flex-1">
                            <p className="text-sm">
                                {t('shortcutSegmentsInfo') ||
                                 'This trip visited waypoints in a different order. Select which route types to save these shortcut segment GPS paths to.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Loading state */}
                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className={cn("ml-2", text.secondary)}>
                            {t('detectingShortcuts') || 'Detecting shortcut edges...'}
                        </span>
                    </div>
                )}

                {/* Error state */}
                {error && (
                    <div className={cn("p-3 rounded-lg", semantic.error.background, semantic.error.text)}>
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {/* Shortcut edges list */}
                {!loading && !error && shortcutEdges.length > 0 && (
                    <div className="space-y-3">
                        {shortcutEdges.map((edge) => {
                            const edgeKey = buildSegmentKey(edge.fromWaypointId, edge.toWaypointId);
                            const state = checkboxStates[edgeKey] || {};

                            return (
                                <div
                                    key={edgeKey}
                                    className={cn("border rounded-lg p-4", border.input, background.card)}
                                >
                                    {/* Edge header */}
                                    <div className={cn("font-medium mb-3 flex items-center gap-2", text.primary)}>
                                        <Route className="w-4 h-4 text-blue-500" />
                                        <span>{edge.fromName}</span>
                                        <span className={text.secondary}>→</span>
                                        <span>{edge.toName}</span>
                                    </div>

                                    {/* Checkbox options */}
                                    <div className="space-y-2 pl-6">
                                        {/* Representative checkbox */}
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={state.representative || false}
                                                onChange={(e) => handleCheckboxChange(edgeKey, 'representative', e.target.checked)}
                                                className="mt-0.5"
                                                disabled={saving}
                                            />
                                            <div className="flex-1">
                                                <div className={cn("font-medium text-sm", text.primary)}>
                                                    {t('representative') || 'Representative'}
                                                    {state.representative && !state.representativeExists && (
                                                        <span className="ml-2 text-xs font-normal text-green-600">
                                                            ({t('default') || 'default'})
                                                        </span>
                                                    )}
                                                </div>
                                                {state.representativeExists ? (
                                                    <div className={cn("text-xs flex items-center gap-1", text.secondary)}>
                                                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                                                        <span>
                                                            {t('existingShortcutWarning', { date: formatDateShort(state.representativeRecordedAt) }) ||
                                                             `Existing shortcut recorded ${formatDateShort(state.representativeRecordedAt)}`}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className={cn("text-xs", text.secondary)}>
                                                        {t('willCreateNewShortcut') || 'Will create new shortcut'}
                                                    </div>
                                                )}
                                            </div>
                                        </label>

                                        {/* Trip's archived_type checkbox (only if different from representative) */}
                                        {tripArchivedType !== 'representative' && (
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={state[tripArchivedType] || false}
                                                    onChange={(e) => handleCheckboxChange(edgeKey, tripArchivedType, e.target.checked)}
                                                    className="mt-0.5"
                                                    disabled={saving}
                                                />
                                                <div className="flex-1">
                                                    <div className={cn("font-medium text-sm capitalize", text.primary)}>
                                                        {tripArchivedType}
                                                    </div>
                                                    {state.tripTypeExists ? (
                                                        <div className={cn("text-xs flex items-center gap-1", text.secondary)}>
                                                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                                                            <span>
                                                                {t('existingShortcutWarning', { date: formatDateShort(state.tripTypeRecordedAt) }) ||
                                                                 `Existing shortcut recorded ${formatDateShort(state.tripTypeRecordedAt)}`}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className={cn("text-xs", text.secondary)}>
                                                            {t('willCreateNewShortcut') || 'Will create new shortcut'}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Validation message */}
                {!loading && !error && !hasSelection() && shortcutEdges.length > 0 && (
                    <div className={cn("p-3 rounded-lg", semantic.warning.background, semantic.warning.text)}>
                        <p className="text-sm">
                            {t('selectAtLeastOneTarget') || 'Please select at least one target for at least one edge'}
                        </p>
                    </div>
                )}
            </div>
        </ConfirmDialogShell>
    );
};

export default SaveShortcutSegmentsDialog;
